// FearGreedHistoryChart.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { LineChart as LineChartIcon } from 'lucide-react';
import {
  ComposedChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { getFearGreedHistory } from '../../services/api';
import { useChartWindow } from '../../hooks/useChartWindow';
import backtest from '../../data/fear-greed-backtest.json';
import './FearGreedHistoryChart.css';

const TAB_OPTIONS = [
  { label: '7일', value: 7 },
  { label: '30일', value: 30 },
  { label: '90일', value: 90 },
  { label: '120일', value: 120 },
  { label: '3Y', value: 1095 },
];

// 인터랙션(좌우 swipe/pan + pinch zoom) 활성화 임계값
const INTERACTIVE_THRESHOLD = 120;
// 큰 탭 진입 시 윈도우 기본 크기 (마지막 N개 표시 → 줌인 상태로 시작)
const DEFAULT_WINDOW_SIZES = { 120: 90, 1095: 90 };

// score → 구간 레이블
const getScoreLabel = (score) => {
  if (score <= 25) return '극심한 공포';
  if (score <= 45) return '공포';
  if (score <= 55) return '중립';
  if (score <= 75) return '탐욕';
  return '과한 탐욕';
};

// score → 색상
const getScoreColor = (score) => {
  if (score <= 25) return '#ef4444';
  if (score <= 45) return '#f97316';
  if (score <= 55) return '#facc15';
  if (score <= 75) return '#22c55e';
  return '#15803d';
};

// 기간별 라벨 포맷
const formatDateForRange = (iso, days) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const m = date.getMonth() + 1;
  const d = date.getDate();
  if (days >= 365) {
    const yy = String(date.getFullYear()).slice(2);
    return `${yy}/${m}/${d}`;
  }
  return `${m}/${d}`;
};

// 차트 데이터 정규화 (다양한 응답 형식 흡수 + 기간별 라벨 포맷)
const normalize = (rawList, days) =>
  rawList
    .map((item) => ({
      iso: item.timestampKst || item.date || item.runDate || item.KST || item.timestamp,
      score: Number(item.score ?? item.value ?? 0),
    }))
    .filter((item) => item.iso && !Number.isNaN(item.score))
    .sort((a, b) => new Date(b.iso) - new Date(a.iso))
    .slice(0, days)
    .sort((a, b) => new Date(a.iso) - new Date(b.iso))
    .map((item) => ({
      iso: item.iso,
      date: formatDateForRange(item.iso, days),
      score: item.score,
    }));

// 차트 점: score < 10 = 빨간 강조, 그 외 마지막만 강조, 데이터 작으면 모두 표시
const ScoreDot = (props) => {
  const { cx, cy, payload, index, dataLength } = props;
  if (cx == null || cy == null) return null;
  // 점수 10 이하: 항상 빨간 강조 (글로우 + 굵은 점) — override 임계값과 일치
  if (payload?.score != null && payload.score <= 10) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={8} fill="rgba(239, 68, 68, 0.20)" />
        <circle cx={cx} cy={cy} r={3.5} fill="#ef4444" stroke="#fff" strokeWidth={1.5} />
      </g>
    );
  }
  // 마지막 포인트 강조
  if (index === dataLength - 1) {
    return (
      <circle cx={cx} cy={cy} r={4} fill={getScoreColor(payload?.score ?? 0)} stroke="#fff" strokeWidth={2} />
    );
  }
  // 데이터가 작으면(≤30) 모든 포인트 표시, 아니면 숨김
  if (dataLength > 30) return null;
  return (
    <circle cx={cx} cy={cy} r={2.5} fill={getScoreColor(payload?.score ?? 0)} stroke="#fff" strokeWidth={1} />
  );
};

// 점수 → 백테스트 bucket 매칭 (CNN 5-tier — getScoreLabel과 동일 구간)
// extreme_fear: s<=25 / fear: 25<s<=45 / neutral: 45<s<=55 / greed: 55<s<=75 / extreme_greed: s>75
const findBucketIndex = (score) => {
  if (score == null || Number.isNaN(score)) return -1;
  if (score <= 25) return 0;
  if (score <= 45) return 1;
  if (score <= 55) return 2;
  if (score <= 75) return 3;
  return 4;
};

// 카드 기간 탭 옵션
const PERIOD_OPTIONS = [
  { id: 'r30',  label: '1M' },
  { id: 'r90',  label: '3M' },
  { id: 'r180', label: '6M' },
  { id: 'r365', label: '1Y' },
];

// ISO → "2026.05.15" 같은 표시 포맷
const formatBacktestDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const score = payload[0]?.value;
  const isAlert = score != null && score < 10;
  return (
    <div className={`fgh-chart-tooltip ${isAlert ? 'fgh-chart-tooltip-alert' : ''}`}>
      <p className="fgh-chart-tooltip-date">{label}</p>
      <p className="fgh-chart-tooltip-score" style={{ color: getScoreColor(score) }}>
        {Math.round(score)}점 · {getScoreLabel(score)}
      </p>
    </div>
  );
};

// Tooltip이 active해질 때 컨텍스트 카드용 포인트로 캡처
const ChartTooltipBridge = ({ active, payload, label, isInteracting, onActiveChange }) => {
  const iso = payload?.[0]?.payload?.iso;
  React.useEffect(() => {
    if (isInteracting) return;
    if (active && iso && payload?.[0]?.payload) {
      onActiveChange(payload[0].payload);
    }
    // active=false일 때는 의도적으로 클리어 X (sticky 유지 — pan 시 useEffect로 reset)
  }, [active, iso, isInteracting, onActiveChange, payload]);

  if (isInteracting || !active) return null;
  return <ChartTooltip active={active} payload={payload} label={label} />;
};

const FearGreedHistoryChart = ({ data: externalData, autoFetch = true }) => {
  const [activeDays, setActiveDays] = useState(7);
  const [cachedList, setCachedList] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cachedSizeRef = useRef(0);

  const isInteractive = activeDays >= INTERACTIVE_THRESHOLD && history.length > 30;
  const defaultWindow = DEFAULT_WINDOW_SIZES[activeDays] ?? 90;
  const { viewWindow, chartRef, bindings, isInteracting } = useChartWindow({
    dataLength: history.length,
    defaultWindow,
    isInteractive,
  });

  // 데이터 로드
  useEffect(() => {
    let cancelled = false;

    if (externalData && Array.isArray(externalData)) {
      setHistory(normalize(externalData, activeDays));
      return undefined;
    }

    if (!autoFetch) return undefined;

    const needsFetch = cachedList.length === 0 || activeDays > cachedSizeRef.current;
    if (!needsFetch) {
      setHistory(normalize(cachedList, activeDays));
      return undefined;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const size = activeDays > 30 ? activeDays : undefined;
        const res = await getFearGreedHistory(size);
        if (cancelled) return;

        const list = Array.isArray(res)
          ? res
          : res?.list || res?.history || res?.data || [];

        setCachedList(list);
        cachedSizeRef.current = size || list.length;
        setHistory(normalize(list, activeDays));
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDays, externalData, autoFetch]);

  // 보이는 데이터 (윈도우 슬라이스)
  const visibleData = useMemo(() => {
    if (history.length === 0) return [];
    return history.slice(viewWindow.start, viewWindow.end + 1);
  }, [history, viewWindow]);

  // 윈도우 기반 통계 (10 미만 횟수 포함)
  const stats = useMemo(() => {
    if (visibleData.length === 0) return null;
    const scores = visibleData.map((d) => d.score);
    return {
      min: Math.min(...scores),
      max: Math.max(...scores),
      avg: scores.reduce((a, b) => a + b, 0) / scores.length,
      latest: scores[scores.length - 1],
      below10: scores.filter((s) => s < 10).length,
    };
  }, [visibleData]);

  // 호버/탭한 포인트 (없으면 null → 최신값 fallback)
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // 컨텍스트 기준 포인트: 선택된 것 > 최신
  const contextPoint = useMemo(() => {
    if (hoveredPoint) return hoveredPoint;
    if (history.length === 0) return null;
    return history[history.length - 1];
  }, [hoveredPoint, history]);

  // 컨텍스트 포인트의 백테스트 통계 (4개 기간 객체 전체 반환)
  // 극단 저점(≤5, ≤10)일 때는 더 세분화된 cumulative 통계로 override
  // (라벨은 5-tier 유지 — tooltip과 일치)
  const backtestContext = useMemo(() => {
    if (!contextPoint) return null;
    const score = contextPoint.score;
    const idx = findBucketIndex(score);
    if (idx < 0) return null;
    const range = backtest.ranges[idx];

    let qqq, voo, statsTag;
    if (score <= 5) {
      qqq = backtest.assets.QQQ.byCumulative.le_5;
      voo = backtest.assets.VOO.byCumulative.le_5;
      statsTag = '≤5';
    } else if (score <= 10) {
      qqq = backtest.assets.QQQ.byCumulative.le_10;
      voo = backtest.assets.VOO.byCumulative.le_10;
      statsTag = '≤10';
    } else {
      qqq = backtest.assets.QQQ.byRange[range.id];
      voo = backtest.assets.VOO.byRange[range.id];
      statsTag = null;
    }
    if (!qqq || !voo) return null;
    return {
      score,
      iso: contextPoint.iso,
      label: range.label,
      isSelected: !!hoveredPoint,
      statsTag,
      qqq, // { r30, r90, r180, r365 }
      voo,
    };
  }, [contextPoint, hoveredPoint]);

  // 카드 기간 선택 (기본 1Y)
  const [selectedPeriod, setSelectedPeriod] = useState('r365');

  // 선택된 포인트는 pan/zoom 중에도 유지 — 사용자가 카드를 보면서 다른 시점 탐색 가능
  // (clear는 데이터/탭 변경 시에만 — 아래 useEffect 참조)
  useEffect(() => {
    setHoveredPoint(null);
  }, [activeDays]);

  return (
    <div className="feature-section">
      <div className="flex items-center gap-1 mb-2">
        <div className="feature-icon">
          <LineChartIcon size={20} />
        </div>
        <h2 className="text-xm font-bold text-black">공포 탐욕 지수 히스토리</h2>
      </div>
      <p className="text-xs text-gray-600 mb-4">
        기간별 시장 심리 변화를 한눈에 확인하세요.
      </p>

      <div className="card">
        {/* Tabs */}
        <div className="fgh-tabs" role="tablist" aria-label="기간 선택">
          {TAB_OPTIONS.map((tab) => (
            <button
              key={tab.value}
              role="tab"
              aria-selected={activeDays === tab.value}
              className={`fgh-tab ${activeDays === tab.value ? 'fgh-tab-active' : ''}`}
              onClick={() => setActiveDays(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Stats */}
        {stats && !loading && (
          <div className="fgh-stats">
            <div className="fgh-stat-item">
              <span className="fgh-stat-label">기간내 평균</span>
              <span className="fgh-stat-value">{stats.avg.toFixed(0)}</span>
            </div>
            <div className="fgh-stat-divider" />
            <div className="fgh-stat-item">
              <span className="fgh-stat-label">10↓ 발생</span>
              <span
                className="fgh-stat-value"
                style={{ color: stats.below10 > 0 ? '#ef4444' : '#9ca3af' }}
              >
                {stats.below10}회
              </span>
            </div>
          </div>
        )}

        {/* Chart — useChartWindow 훅으로 인터랙션 공통화 */}
        <div
          ref={chartRef}
          className={`fgh-chart-wrap ${isInteractive ? 'fgh-chart-interactive' : ''}`}
          {...bindings}
        >
          {loading ? (
            <div className="fgh-empty">불러오는 중...</div>
          ) : error ? (
            <div className="fgh-empty">데이터를 불러오지 못했습니다</div>
          ) : visibleData.length === 0 ? (
            <div className="fgh-empty">데이터가 없습니다</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={visibleData}
                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
              >
                <defs>
                  <linearGradient id="fghAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#B8A7E8" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#B8A7E8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={visibleData.length > 60 ? 40 : 24}
                  tickMargin={10}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  width={30}
                  tickMargin={8}
                  padding={{ top: 10, bottom: 10 }}
                />
                <ReferenceLine y={10} stroke="#fca5a5" strokeDasharray="2 3" strokeWidth={1} />
                <ReferenceLine y={25} stroke="#fecaca" strokeDasharray="4 4" />
                <ReferenceLine y={50} stroke="#e5e7eb" strokeDasharray="4 4" />
                <ReferenceLine y={75} stroke="#bbf7d0" strokeDasharray="4 4" />
                <Tooltip
                  cursor={!isInteracting}
                  content={(props) => (
                    <ChartTooltipBridge
                      {...props}
                      isInteracting={isInteracting}
                      onActiveChange={setHoveredPoint}
                    />
                  )}
                />
                <Area
                  type="monotone"
                  dataKey="score"
                  stroke="#6b5fcf"
                  strokeWidth={2}
                  fill="url(#fghAreaGrad)"
                  dot={ScoreDot}
                  activeDot={{ r: 5, fill: '#6b5fcf', stroke: '#fff', strokeWidth: 2 }}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Legend */}
        <div className="fgh-legend">
          <span className="fgh-legend-item">
            <span className="fgh-legend-dot fgh-legend-alert" />
            10↓ 극심한 공포
          </span>
          <span className="fgh-legend-item">
            <span className="fgh-legend-dot" style={{ background: '#facc15' }} />
            중립
          </span>
          <span className="fgh-legend-item">
            <span className="fgh-legend-dot" style={{ background: '#15803d' }} />
            과한 탐욕
          </span>
        </div>

        {/* 백테스트 컨텍스트 카드 — 선택된 포인트 + 선택 기간 */}
        {backtestContext && (() => {
          const qqqStats = backtestContext.qqq[selectedPeriod];
          const vooStats = backtestContext.voo[selectedPeriod];
          const periodLabel = PERIOD_OPTIONS.find((p) => p.id === selectedPeriod)?.label || '';
          if (!qqqStats || !vooStats || qqqStats.n === 0) return null;
          return (
            <div className="fgh-context-card">
              <div className="fgh-context-title">
                <span className={`fgh-context-date ${backtestContext.isSelected ? 'is-selected' : ''}`}>
                  {formatBacktestDate(backtestContext.iso)}
                </span>
                <span className="fgh-context-score-pill">
                  {backtestContext.score.toFixed(0)} · {backtestContext.label}
                  {backtestContext.statsTag && (
                    <em className="fgh-context-statstag">{backtestContext.statsTag} 통계</em>
                  )}
                </span>
              </div>

              {/* 기간 탭 */}
              <div className="fgh-context-periods" role="tablist" aria-label="기간 선택">
                {PERIOD_OPTIONS.map((p) => (
                  <button
                    key={p.id}
                    role="tab"
                    aria-selected={selectedPeriod === p.id}
                    className={`fgh-context-period ${selectedPeriod === p.id ? 'is-active' : ''}`}
                    onClick={() => setSelectedPeriod(p.id)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="fgh-context-headline">
                같은 구간 진입 후 <strong>{periodLabel}</strong> 과거 평균 (2011~)
              </div>

              <div className="fgh-context-assets">
                <div className="fgh-context-asset">
                  <span className="fgh-context-ticker">QQQ</span>
                  <span className={`fgh-context-mean ${qqqStats.mean >= 0 ? 'pos' : 'neg'}`}>
                    {qqqStats.mean >= 0 ? '+' : ''}
                    {(qqqStats.mean * 100).toFixed(1)}%
                  </span>
                  <span className="fgh-context-meta">
                    승률 {(qqqStats.winRate * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="fgh-context-vbar" />
                <div className="fgh-context-asset">
                  <span className="fgh-context-ticker">VOO</span>
                  <span className={`fgh-context-mean ${vooStats.mean >= 0 ? 'pos' : 'neg'}`}>
                    {vooStats.mean >= 0 ? '+' : ''}
                    {(vooStats.mean * 100).toFixed(1)}%
                  </span>
                  <span className="fgh-context-meta">
                    승률 {(vooStats.winRate * 100).toFixed(0)}%
                  </span>
                </div>
              </div>

              <div className="fgh-context-foot">
                <span className={`fgh-context-state ${backtestContext.isSelected ? 'is-selected' : ''}`}>
                  {backtestContext.isSelected ? '선택한 시점' : '최신 시점'}
                </span>
                {' · '}n={qqqStats.n}
                {' · '}차트의 다른 지점을 탭하면 그 시점 통계로 변경
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default FearGreedHistoryChart;
